import { GraphQLResult } from 'aws-amplify/api'
import { FieldItem, type FieldOwnerItem } from './property.type'
import {
  fieldsByOrganizationQuery,
  getFieldOwnerQuery,
  getFieldQuery,
} from '@/services/graphql/property'
import { getAmplifyClient } from '@/services/amplifyClient'
import { OptionFieldFilter } from '../queries/utils/filters'
import type { ListFieldsScopeInput } from './property.keys'

export const getFieldItem = async ({
  id,
  lang,
  optionFilter,
}: {
  id: string | undefined
  lang?: string
  optionFilter?: OptionFieldFilter
}) => {
  if (!id) return undefined
  const client = getAmplifyClient()

  const { data } = (await client.graphql<{ getField: FieldItem }>({
    query: getFieldQuery,
    variables: {
      id,
      lang: lang ? { lang: { eq: lang } } : undefined,
      optionFilter,
    },
  })) as GraphQLResult<{ getField: FieldItem }>

  return data?.getField
}

export const getFieldOwnerItem = async (id: string | undefined) => {
  if (!id) return undefined
  const client = getAmplifyClient()

  const { data } = (await client.graphql<{ getField: FieldOwnerItem }>({
    query: getFieldOwnerQuery,
    variables: { id },
  })) as GraphQLResult<{ getField: FieldOwnerItem }>

  return data?.getField
}

// -----------------------------------------------------------------------------
// Fetcher: listFields
// - Récupère tous les LIST Fields d'une organisation (+ global SKOP)
// - Utilisé par useListFields
// -----------------------------------------------------------------------------

const fetchListsByOrganization = async (organizationFieldsId: string) => {
  const client = getAmplifyClient()
  const { data } = (await client.graphql({
    query: fieldsByOrganizationQuery,
    variables: {
      organizationFieldsId,
      filter: { type: { eq: 'LIST' } },
    },
  })) as GraphQLResult<{
    fieldsByOrganization: { items: FieldItem[] }
  }>

  return data?.fieldsByOrganization?.items ?? []
}

export const listFields = async ({
  scopeKind,
  activeOrganizationId,
  inheritedOrganizationIds = [],
}: ListFieldsScopeInput) => {
  if (scopeKind === 'unresolved') return []

  const globalOrganizationId = process.env.NEXT_PUBLIC_SKOP_ORGID
  const globalLists = globalOrganizationId
    ? await fetchListsByOrganization(globalOrganizationId)
    : []

  if (scopeKind === 'solo') {
    return globalLists
  }

  const organizationIds = Array.from(
    new Set(
      [activeOrganizationId, ...inheritedOrganizationIds].filter(
        (organizationId): organizationId is string => Boolean(organizationId),
      ),
    ),
  )

  if (organizationIds.length === 0) {
    return globalLists
  }

  const scopedOrganizationIds = organizationIds.filter(
    (organizationId) => organizationId !== globalOrganizationId,
  )

  if (scopedOrganizationIds.length === 0) {
    return globalLists
  }

  const orgLists = (
    await Promise.all(
      scopedOrganizationIds.map((organizationId) =>
        fetchListsByOrganization(organizationId),
      ),
    )
  ).flat()

  return [
    ...new Map(
      [...globalLists, ...orgLists].map((item) => [item.id, item] as const),
    ).values(),
  ]
}
